import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle,
} from "@mui/material";
import { ReactNode } from "react";
import { GLOBAL_FORM_MODAL_COLORS } from "../../utils/util.standardized";
import Flex from "../Flex";
import Text from "../Text";

type StyledModalProps = {
  dialogTitle?: ReactNode;
  children: ReactNode;
  open: boolean;
  onClose?: () => void;
  dialogAction?: ReactNode;
  dialogActionsProps?: DialogActionsProps;
  disableCloseIcon?: boolean;
  dialogContentProps?: DialogContentProps;
  endIcon?: ReactNode;
} & DialogProps;

const StyledModal = ({
  dialogTitle,
  children,
  dialogAction,
  disableCloseIcon,
  dialogContentProps,
  dialogActionsProps,
  onClose,
  open,
  endIcon,
  ...others
}: StyledModalProps) => {
  return (
    <Dialog
      open={open}
      maxWidth={false}
      {...others}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          ...((others?.sx as any)?.["& .MuiDialog-paper"] ?? {}),
        },
      }}
      sx={{
        width: "100%",
        ...(typeof others?.sx === "object" ? others.sx : {}),
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: GLOBAL_FORM_MODAL_COLORS.HEADER_BG,
          p: "8px 12px 8px 24px",
          height: "34px",
        }}
      >
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Flex alignItems={"center"}>
            {typeof dialogTitle !== "string" ? (
              dialogTitle
            ) : (
              <Text
                fontSize={"14px"}
                fontWeight={500}
                c={GLOBAL_FORM_MODAL_COLORS.HEADER_COLOR}
              >
                {dialogTitle}
              </Text>
            )}
          </Flex>

          <Flex gap={"10px"} alignItems={"center"} justifyContent={"center"}>
            {endIcon && endIcon}

            {!disableCloseIcon && (
              <CloseIcon
                onClick={onClose}
                sx={{
                  cursor: "pointer",
                  color: GLOBAL_FORM_MODAL_COLORS.HEADER_COLOR,
                  fontSize: "18px",
                }}
              />
            )}
          </Flex>
        </Flex>
      </DialogTitle>

      <DialogContent
        {...dialogContentProps}
        sx={{
          backgroundColor: GLOBAL_FORM_MODAL_COLORS.CONTENT_BG,
          p: "16px",
          pt: "24px",
          overflow: "auto",
          // MUI <Grid container spacing={X}> applies a negative margin-top
          // which pulls the first row's floating labels behind the DialogTitle.
          // Reset it only for direct-child Grid containers so nested Grid
          // spacing inside form fields is unaffected.
          "& > .MuiGrid-container": {
            mt: 0,
          },
          ...dialogContentProps?.sx,
        }}
      >
        {children}
      </DialogContent>

      {dialogAction && (
        <DialogActions
          {...dialogActionsProps}
          sx={{
            backgroundColor: GLOBAL_FORM_MODAL_COLORS.FOOTER_BG,
            p: "12px",
            borderTop: "1px solid " + GLOBAL_FORM_MODAL_COLORS.SECTION_BORDER,
            ...dialogActionsProps?.sx,
          }}
        >
          {dialogAction}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StyledModal;
